import { Badge, BadgeProps, HStack, VisuallyHidden } from '@tq/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationStatus } from 'types';
import { UnknownStatusItem, useApplicationStatuses } from './hooks';

export interface StatusIndicatorProps extends BadgeProps {
  status: ApplicationStatus;
  separateLabel?: boolean;
}

export const ApplicationStatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  separateLabel = false,
  ...parentBadgeProps
}) => {
  const statuses = useApplicationStatuses();
  const { t } = useTranslation('case_summary');

  const getApplicationStatus = (
    applicationStatusText: string
  ): {
    label: string;
    badgeProps: BadgeProps;
  } => {
    if (Object.keys(statuses).indexOf(applicationStatusText) > -1) {
      return statuses[applicationStatusText];
    }
    return { ...UnknownStatusItem(), label: applicationStatusText };
  };

  const applicationStatus = getApplicationStatus(status);

  return (
    <>
      {separateLabel ? (
        <HStack>
          <Badge
            data-testid='application-status-badge'
            {...applicationStatus.badgeProps}
            {...parentBadgeProps}
            w={5}
            h={5}
          />

          <strong aria-hidden='true'>{applicationStatus.label}</strong>
          <VisuallyHidden>
            {t('application_status', 'Application status')}: {applicationStatus.label}
          </VisuallyHidden>
        </HStack>
      ) : (
        <Badge
          data-testid='application-status-badge'
          display='flex'
          alignItems='center'
          justifyContent='center'
          fontSize='sm'
          h={6}
          {...applicationStatus.badgeProps}
          {...parentBadgeProps}
        >
          <strong aria-hidden='true'>{applicationStatus.label}</strong>
          <VisuallyHidden>
            {t('application_status', 'Application status')}: {applicationStatus.label}
          </VisuallyHidden>
        </Badge>
      )}
    </>
  );
};
